import { twMerge } from '@shared/themes';
import { FRONTIFY_URL } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { DreamPodWrapper } from './components/DreamPodWrapper';
import { getDreamPodsConfig } from './constants';

type DreamMadeProps = ComponentProps<'section'> & {
  namespace: string;
};

/**
 * Full width section component with background displaying "Dreams made by Lottoland".
 *
 * @param namespace - The translation namespace to use
 */
function DreamMade({ namespace, className, ...props }: DreamMadeProps) {
  const t = useTranslations(namespace);

  const dreamPodsConfig = getDreamPodsConfig(t);

  return (
    <section
      className={twMerge('dark dark:bg-container-accent-inverse relative h-[448px] sm:h-[728px] md:h-[919px] overflow-hidden', className)}
      data-testid="dream-made-section"
      data-track-container="Winners Stories"
      {...props}
    >
      <Image
        src={`${FRONTIFY_URL}MWGkspR1VVLgAahuk6SZ.svg`}
        alt="Dream made decorative bottom left"
        width={400}
        height={400}
        className="object-contain absolute bottom-0 left-0 z-0 max-w-[28%]"
        priority
      />
      <Image
        src={`${FRONTIFY_URL}jc2c9KXJTs3W4QVQkyxg.svg`}
        alt="Dream made decorative top right"
        width={400}
        height={400}
        className="object-contain absolute top-0 right-0 z-0 max-w-[28%]"
        priority
      />
      <div className="dream-made-grid-container gap-2xl h-full relative z-10 ll-container-inner-full pl-5 sm:pl-[70px] md:px-0" data-testid="dream-made-container">
        <div
          className="dream-made-title-area pt-10 sm:pt-17.5 md:pt-0 md:px-21 md:h-[40%] md:flex md:justify-end flex flex-col md:flex-row md:items-center md:gap-3 items-start md:items-end"
          data-testid="dream-made-title"
        >
          <h2 className="heading-2 lg:heading-1 text-left md:text-right md:whitespace-nowrap" data-testid="dream-made-title-text">
            <span className="block md:inline">{t('title_dreams')}</span> <span className="block md:inline">{t('title_made')}</span>
          </h2>
        </div>
        <div
          className="dream-made-cards-area w-full flex flex-row md:flex-row gap-4 md:gap-x-11 md:pt-15 md:ml-10 md:pb-15 md:pl-18 overflow-hidden"
          data-testid="dream-made-cards"
        >
          <div className="flex flex-row gap-4 md:hidden flex-shrink-0 items-end" data-testid="dream-made-row-mobile">
            {dreamPodsConfig.map((pod, index) => (
              <DreamPodWrapper key={index} pod={pod} additionalClassName={twMerge('flex-shrink-0', index === 0 && 'mb-7 md:mb-0')} />
            ))}
          </div>
          <div className="hidden md:flex flex-col gap-y-10 flex-shrink-0 items-end" data-testid="dream-made-column-1">
            {dreamPodsConfig[0] && <DreamPodWrapper pod={dreamPodsConfig[0]} />}
            {dreamPodsConfig[2] && <DreamPodWrapper pod={dreamPodsConfig[2]} />}
          </div>
          <div className="hidden md:flex flex-col gap-y-11 flex-shrink-0 mr-21" data-testid="dream-made-column-2">
            {dreamPodsConfig[1] && <DreamPodWrapper pod={dreamPodsConfig[1]} additionalClassName="mt-34.5" />}
            {dreamPodsConfig[3] && <DreamPodWrapper pod={dreamPodsConfig[3]} />}
          </div>
        </div>
        <div
          className="dream-made-subtitle-area md:px-5 md:h-[60%] md:flex md:px-21 text-right flex flex-col items-end pb-10 sm:pb-28.5 sm:pr-17.5 pr-5 md:pb-22.5 md:pt-0 mt-3 sm:mt-10 md:mt-0"
          data-testid="dream-made-subtitle"
        >
          <h4 className="md:display-m-strong lg:display-l-strong">
            <span className="block">{t('subtitle_by')}</span>
            <span className="block">{t('subtitle_lottoland')}</span>
          </h4>
        </div>
      </div>
    </section>
  );
}

export { DreamMade, type DreamMadeProps };
